import { useEffect } from "react";
import trackPushNotification from "@ch/helpers/utils/firebase/trackPushNotification";

interface TrackNotificationPayload {
  id: string;
  isDelivered?: boolean;
  isClicked?: boolean;
}

interface TrackNotificationMessage {
  type: "TRACK_NOTIFICATION";
  payload: TrackNotificationPayload;
}

/**
 * Listens for postMessage events from the service worker to track
 * background notification delivery and clicks.
 */
const useBackgroundNotificationTracking = () => {
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only handle messages from our service worker
      if (event.origin !== window.location.origin) {
        return;
      }

      // Type guard to check if message is our track notification message
      const data = event.data as unknown;
      if (
        !data ||
        typeof data !== "object" ||
        !("type" in data) ||
        data.type !== "TRACK_NOTIFICATION"
      ) {
        return;
      }

      const message = data as TrackNotificationMessage;
      const { payload } = message;

      if (payload?.id) {
        void trackPushNotification({
          id: payload.id,
          isDelivered: payload.isDelivered,
          isClicked: payload.isClicked,
        });
      }
    };

    navigator.serviceWorker.addEventListener("message", handleMessage);

    return () => {
      navigator.serviceWorker.removeEventListener("message", handleMessage);
    };
  }, []);
};

export default useBackgroundNotificationTracking;
